// src/lib/engine.ts — verified against Halcrow Yuen Long South Stage 2B sheets
// Roughness + storm data sourced from sdmTables.ts (full SDM Tables 3/13/14 + TGN30 T1)
// v3.1.1 — PURE MODULE: no DOM, window, localStorage or React, so the engine sidecar imports this exact file.
//   + link length follows bend vertices (matches the Tables tab and the XLSX export)
//   + catchment allocation + optimiser moved here from DrainageEditor.tsx (one engine for UI and server)
//   + optimiser: multi-barrel utilisation no longer divided by nPipes twice; zero/negative steps guarded
//   + resolvedStormTable(): reports when a storm region falls back to HKO
import { MANNING_N, KS_VALUES, STORM_CONSTANTS, CLIMATE_UPLIFT, Condition, StormConst } from './sdmTables';
export { MANNING_N, KS_VALUES, STORM_CONSTANTS, CLIMATE_UPLIFT };
export type { Condition };

export const ENGINE_VERSION = '3.1.1';

export type ReturnPeriod = 2|5|10|20|50|100|200|500|1000;
export type DesignType = 'slope' | 'siteformation';
export type StormRegion = 'HKO' | 'TaiMoShan' | 'WestLantau' | 'NorthDistrict' | 'DrySeasonWhole' | 'DrySeasonCore';
export type ClimatePeriod = 'before2041' | 'mid21' | 'end21';
export type SectionType = 'uchannel' | 'rectangular' | 'trapezoidal' | 'circular';
export type NodeType = 'manhole' | 'catchpit' | 'outfall';

type StormTable = Record<number, StormConst>;
const STORM_TABLES = STORM_CONSTANTS as unknown as Record<string, StormTable | undefined>;

/** Key of the storm table actually used: 'TGN30' for slopes, the region if sdmTables has it, else 'HKO'. */
export function resolvedStormTable(dt:DesignType, r:StormRegion):string{
  if(dt==='slope') return 'TGN30';
  return STORM_TABLES[r] ? r : 'HKO';
}
function stormTable(dt:DesignType, r:StormRegion):StormTable{
  return STORM_TABLES[resolvedStormTable(dt,r)] ?? {};
}
export function availableReturnPeriods(dt:DesignType,r:StormRegion):ReturnPeriod[]{
  return Object.keys(stormTable(dt,r)).map(Number).sort((a,b)=>a-b) as ReturnPeriod[];
}
export function getStormConstants(dt:DesignType,r:StormRegion,T:number):StormConst{
  const sc=stormTable(dt,r)[T];
  if(!sc) throw new Error(`T=${T} not defined for ${resolvedStormTable(dt,r)}`);
  return sc;
}
export function climateIncrease(dt:DesignType,p:ClimatePeriod):number{
  return CLIMATE_UPLIFT[dt==='slope'?'slope':'siteformation'][p];
}

export const RUNOFF_COEFF: Record<string,number> = {
  'Paved':0.95,'Rock Cut Slope':0.90,'Temporary Soil Cut Slope':0.60,
  'Hydroseeding soil slope / planter':0.35,'Grassland (Steep)':0.35,'Grassland (Flat)':0.25,
  'Grassland (Sandy, Steep)':0.20,'Grassland (Sandy, Flat)':0.15,'Pond / Water feature':1.00 };

export interface Section { type:SectionType; width:number; depth?:number; sideSlope?:number; } // mm

// --- geometry (unchanged) ---------------------------------------------------
export function uChannelGeom(W_mm:number, depth_m:number){
  const d = depth_m*1000;
  const h = Math.max(0, d - W_mm/2);              // clamp — shallow depth previously went negative
  const P = (Math.max(W_mm, 2*h) + W_mm*Math.PI/2)/1000;
  const A = (W_mm*Math.max(W_mm/2, h) + Math.PI*W_mm*W_mm/8)/1e6;
  return { A, P, R: P>0?A/P:0 };
}
export function fixedGeom(s:Section){
  const W=s.width/1000, D=(s.depth??s.width)/1000, z=s.sideSlope??0;
  switch(s.type){
    case 'rectangular': { const A=W*D,P=W+2*D; return {A,P,R:A/P}; }
    case 'trapezoidal': { const A=(W+z*D)*D,P=W+2*D*Math.sqrt(1+z*z); return {A,P,R:A/P}; }
    case 'circular':    { const A=Math.PI*W*W/4; return {A,P:Math.PI*W,R:W/4}; }
    default:            { const A=W*D,P=W+2*D; return {A,P,R:A/P}; }
  }
}

// --- hydraulics (unchanged) -------------------------------------------------
export const manningVelocity = (n:number,R:number,S:number)=>(1/n)*Math.pow(R,2/3)*Math.sqrt(S);
export function colebrookVelocity(ks_mm:number,R:number,S:number,g=9.81,v=1e-6){
  if(R<=0||S<=0) return 0;
  const t=Math.sqrt(32*g*R*S);
  return -t*Math.log10(ks_mm/1000/(14.8*R)+1.255*v/(R*t));
}
export const rainfallIntensity=(sc:StormConst,cc:number,t:number)=>(1+cc)*sc.a/Math.pow(Math.max(t,1)+sc.b,sc.c);
export const overlandTime=(L:number,H:number,A:number)=>A>0&&H>0?0.14465*L/(Math.pow(H,0.2)*Math.pow(A,0.1)):0;
export const flowTime=(len:number,V:number)=>V>0?(len/V)/60:0;
export const runoff_Lmin=(C:number,i:number,A:number)=>0.278*C*i*(A/1e6)*60000;

// --- data model -------------------------------------------------------------
export interface DrainNode{ id:string;type:NodeType;e:number;n:number;
  groundLevel:number;invertLevel:number;size:number;label:string; }
export interface Catchment{ id:string;area:number;surfaceType:string;C:number;H:number;L:number;
  poly?:{e:number;n:number}[]; }
export interface DrainLink{ id:string;label:string;
  fromNodeId:string|null;fromPoint?:{e:number;n:number};
  toNodeId:string|null;toPoint?:{e:number;n:number};
  vertices?:{e:number;n:number}[];                  // bend points (length follows them)
  section:Section;material:string;condition:Condition;n:number;ks:number;
  nPipes?:number;                                   // barrel count, default 1
  usInvert:number;dsInvert:number;usGroundLevel?:number;dsGroundLevel?:number;
  lengthOverride?:number;catchments:Catchment[]; }
export interface DesignSettings{ returnPeriod:ReturnPeriod;designType:DesignType;
  stormRegion:StormRegion;climatePeriod:ClimatePeriod; }

/** Project-level catchment split across links by percentage (v4/v5 `gcatch`). */
export interface CatchAlloc{ linkId:string;pct:number; }
export interface GlobalCatchment{ id:string;name:string;area:number;surfaceType:string;C:number;H:number;L:number;
  poly?:{e:number;n:number}[];allocs:CatchAlloc[]; }

export interface LinkResult{ linkId:string;length:number;S:number;depth:number;
  A:number;P:number;R:number;Vman:number;Vcw:number;Vgov:number;equation:string;
  to:number;tStar:number;tInlet:number;tFlow:number;tc:number;i:number;
  ownArea:number;accArea:number;ownRunoff:number;accRunoff:number;Qcap:number;usage:number;error?:string; }

export function linkLength(l:DrainLink,byId:Map<string,DrainNode>):number{
  if(l.lengthOverride!=null) return l.lengthOverride;
  const to=l.toNodeId?byId.get(l.toNodeId):l.toPoint;
  const from=l.fromNodeId?byId.get(l.fromNodeId):l.fromPoint;
  if(!to||!from) return 0;
  const pts=[{e:from.e,n:from.n},...(l.vertices??[]),{e:to.e,n:to.n}];
  let s=0;
  for(let i=1;i<pts.length;i++) s+=Math.hypot(pts[i].e-pts[i-1].e,pts[i].n-pts[i-1].n);
  return s;
}

/** Converts % allocations into per-link equivalent catchments (same rule the editor always used). */
export function withAllocatedCatchments<L extends DrainLink>(links:L[],gcatch:GlobalCatchment[]):L[]{
  return links.map(l=>({...l,catchments:gcatch.flatMap(g=>{
    const a=(g.allocs??[]).find(x=>x.linkId===l.id);
    return a&&a.pct>0?[{id:`${g.name} (${a.pct}%)`,area:Math.round(g.area*a.pct/100),
      surfaceType:g.surfaceType,C:g.C,H:g.H,L:g.L} as Catchment]:[];})}));
}

export function computeNetwork(nodes:DrainNode[],links:DrainLink[],s:DesignSettings):Map<string,LinkResult>{
  const out=new Map<string,LinkResult>();
  const nodeById=new Map(nodes.map(n=>[n.id,n]));
  const upstreamOf=(l:DrainLink)=>l.fromNodeId?links.filter(x=>x.toNodeId===l.fromNodeId):[];
  const sc=getStormConstants(s.designType,s.stormRegion,s.returnPeriod);
  const cc=climateIncrease(s.designType,s.climatePeriod);
  const visiting=new Set<string>();

  function compute(l:DrainLink):LinkResult{
    if(out.has(l.id)) return out.get(l.id)!;
    if(visiting.has(l.id)){ const e={linkId:l.id,error:'cycle'} as LinkResult; out.set(l.id,e); return e; }
    visiting.add(l.id);
    const ups=upstreamOf(l).map(compute);
    const length=linkLength(l,nodeById);
    const S=length>0?Math.max((l.usInvert-l.dsInvert)/length,1e-6):1e-6;

    const usGL=l.fromNodeId?nodeById.get(l.fromNodeId)?.groundLevel:l.usGroundLevel;
    const dsGL=l.toNodeId?nodeById.get(l.toNodeId)?.groundLevel:l.dsGroundLevel;
    const dUS=(usGL??dsGL??0)-l.usInvert, dDS=(dsGL??usGL??0)-l.dsInvert;
    const depth=Math.max(0,(dUS+dDS)/2);

    const g = l.section.type==='uchannel' ? uChannelGeom(l.section.width,depth)
      : l.section.type==='circular' ? fixedGeom(l.section)
      : fixedGeom({...l.section, depth: l.section.depth ?? Math.round(depth*1000)});

    const Vman=manningVelocity(l.n,g.R,S);
    const Vcw=colebrookVelocity(l.ks,g.R,S);
    const pipe=l.section.type==='circular';
    const Vgov=pipe?Vcw:Vman;
    const equation=pipe?'Colebrook-White':'Manning';

    const to=Math.max(0,...l.catchments.map(c=>overlandTime(c.L,c.H,c.area)));
    const tStar=Math.max(0,...ups.map(u=>u.tc||0));
    const tInlet=Math.max(to,tStar);
    const tFlow=flowTime(length,Vgov);
    const tc=Math.max(1,tInlet+tFlow);
    const i=rainfallIntensity(sc,cc,tc);
    const ownRunoff=l.catchments.reduce((a,c)=>a+runoff_Lmin(c.C,i,c.area),0);
    const accRunoff=ownRunoff+ups.reduce((a,u)=>a+(u.accRunoff||0),0);
    const ownArea=l.catchments.reduce((a,c)=>a+c.area,0);
    const accArea=ownArea+ups.reduce((a,u)=>a+(u.accArea||0),0);
    const Qcap=g.A*Vgov*60000*(l.nPipes??1);   // multi-barrel: capacity scales, velocity is per-barrel

    const res:LinkResult={linkId:l.id,length,S,depth,A:g.A,P:g.P,R:g.R,
      Vman,Vcw,Vgov,equation,to,tStar,tInlet,tFlow,tc,i,ownArea,accArea,
      ownRunoff,accRunoff,Qcap,usage:Qcap>0?accRunoff/Qcap*100:0};
    out.set(l.id,res); visiting.delete(l.id); return res;
  }
  links.forEach(compute); return out;
}

// --- optimiser (moved from DrainageEditor.tsx) --------------------------------
export interface OptParams{ maxUsage:number;vMin:number;vMax:number;maxDepth:number;minCover:number;
  minSize:number;maxSize:number;sizeStep:number;maxPipes:number;ilStep:number; }
export const DEFAULT_OPT_PARAMS:OptParams={maxUsage:80,vMin:1,vMax:4,maxDepth:3,minCover:0.3,
  minSize:225,maxSize:1050,sizeStep:75,maxPipes:3,ilStep:0.05};
export interface OptimiseResult<N extends DrainNode,L extends DrainLink>{
  nodes:N[];links:L[];failed:string[];diagnostics:string[]; }

/** Upstream→downstream; smallest size first, deepens before upsizing; pipes go multi-barrel. Inputs are not mutated. */
export function optimiseNetwork<N extends DrainNode,L extends DrainLink>(
  nodes:N[],links:L[],gcatch:GlobalCatchment[],s:DesignSettings,optP:OptParams):OptimiseResult<N,L>{
  const sizeStep=Math.max(1,optP.sizeStep), ilStep=Math.max(0.001,optP.ilStep);
  const wNodes=nodes.map(n=>({...n}));
  const wLinks=links.map(l=>({...l,section:{...l.section}}) as L);
  const nById=new Map(wNodes.map(n=>[n.id,n]));
  const order:L[]=[];const placed=new Set<string>();
  for(let g=0;g<=wLinks.length&&order.length<wLinks.length;g++)
    wLinks.forEach(l=>{if(placed.has(l.id))return;
      const upsDone=wLinks.every(u=>u===l||!u.toNodeId||u.toNodeId!==l.fromNodeId||placed.has(u.id));
      if(upsDone){order.push(l);placed.add(l.id);}});
  wLinks.forEach(l=>{if(!placed.has(l.id))order.push(l);});
  const evalNet=()=>{try{return computeNetwork(wNodes,withAllocatedCatchments(wLinks,gcatch),s);}
    catch{return new Map<string,LinkResult>();}};
  const failed:string[]=[];const diagnostics:string[]=[];
  for(const l of order){
    const isPipe=l.section.type==='circular';
    const fromN=l.fromNodeId?nById.get(l.fromNodeId):undefined;
    const toN=l.toNodeId?nById.get(l.toNodeId):undefined;
    const glUS=fromN?fromN.groundLevel:(l.usGroundLevel??l.usInvert+optP.maxDepth);
    const glDS=toN?toN.groundLevel:(l.dsGroundLevel??glUS);
    const inc=l.fromNodeId?wLinks.filter(u=>u.toNodeId===l.fromNodeId):[];
    const usBase=inc.length?Math.min(...inc.map(u=>u.dsInvert)):fromN?fromN.invertLevel:l.usInvert;
    const dsFloor=glDS-optP.maxDepth;
    const maxN=isPipe?Math.max(1,Math.round(optP.maxPipes)):1;
    let ok=false;let why='no candidate between GL and max depth';
    for(let np=1;np<=maxN&&!ok;np++)
      for(let size=optP.minSize;size<=optP.maxSize+1e-9&&!ok;size+=sizeStep){
        const bury=size/1000+(isPipe?optP.minCover:0);
        const us=+Math.min(usBase,glUS-bury).toFixed(3);
        const dsCeil=Math.min(us-ilStep,glDS-bury);
        if(dsCeil<dsFloor-1e-9){why=`${size}mm: no room between DS GL and max depth`;continue;}
        l.section.width=size;l.nPipes=np;l.usInvert=us;
        for(let ds=dsCeil;ds>=dsFloor-1e-9;ds-=ilStep){
          l.dsInvert=+ds.toFixed(3);
          const r=evalNet().get(l.id);
          if(!r||r.error){why=`${size}mm: ${r?.error??'no result'}`;continue;}
          if(r.Vgov>optP.vMax){why=`${size}mm: V ${r.Vgov.toFixed(2)} > vMax`;break;}
          // r.usage already uses Qcap × nPipes — do not divide by np again
          if(r.usage>optP.maxUsage)why=`${size}mm ×${np}: usage ${r.usage.toFixed(0)}% > max`;
          else if(r.Vgov<optP.vMin)why=`${size}mm: V ${r.Vgov.toFixed(2)} < vMin`;
          else{ok=true;break;}
        }
      }
    if(!ok){
      const bury=optP.maxSize/1000+(isPipe?optP.minCover:0);
      l.section.width=optP.maxSize;l.nPipes=maxN;
      l.usInvert=+Math.min(usBase,glUS-bury).toFixed(3);
      l.dsInvert=+Math.min(l.usInvert-ilStep,dsFloor).toFixed(3);
      failed.push(l.label);diagnostics.push(`${l.label} — last rejection: ${why}`);}
  }
  wNodes.forEach(n=>{const inc=wLinks.filter(u=>u.toNodeId===n.id);
    if(inc.length){n.invertLevel=Math.min(...inc.map(u=>u.dsInvert));return;}
    const out=wLinks.filter(u=>u.fromNodeId===n.id);
    if(out.length)n.invertLevel=Math.min(...out.map(u=>u.usInvert));});
  return {nodes:wNodes,links:wLinks,failed,diagnostics};
}

// --- whole-project evaluation (used by the sidecar; safe in the browser) -------
export interface DrainageProject{ version:number;nodes:DrainNode[];links:DrainLink[];
  gcatch?:GlobalCatchment[];settings:DesignSettings;[k:string]:unknown; }
export function evaluateProject(p:DrainageProject){
  const links=Array.isArray(p.gcatch)
    ? withAllocatedCatchments((p.links??[]).map(l=>({...l,catchments:[]})),p.gcatch)
    : (p.links??[]).map(l=>({...l,catchments:l.catchments??[]}));   // v<4: catchments stored per link
  const results=computeNetwork(p.nodes??[],links,p.settings);
  return { engineVersion:ENGINE_VERSION,
    stormTable:resolvedStormTable(p.settings.designType,p.settings.stormRegion),
    results:[...results.values()] };
}